import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { adminUpdateUserProfile } from "../../actions/user.actions";
import { getRegions } from "../../actions";

const getRegionId = (region) => (
    !region ? "" : typeof region === "object"
        ? (region._id || region.value || region.id || "")
        : region
);

const isPhoneInputValid = (value) => {
    const raw = String(value || "").trim();

    if (!raw) return true;
    if (!/^[+\d\s()-]+$/.test(raw)) return false;

    const plusMatches = raw.match(/\+/g) || [];
    if (plusMatches.length > 1 || (plusMatches.length === 1 && !raw.startsWith("+"))) {
        return false;
    }

    const digits = raw.replace(/\D/g, "");

    if (raw.startsWith("+")) {
        return digits.length === 11 && digits.startsWith("7");
    }

    return digits.length === 10 ||
        (digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8")));
};

const buildForm = (user = {}) => ({
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    birthData: user.birthData || "",
    gender: user.gender || "",
    region: getRegionId(user.region),
    contactNumber: user.contactNumber || "",
    backupNumber: user.backupNumber || "",
    nameOrganization: user.nameOrganization || "",
    isActive: user.isActive !== false,
});

const labels = {
    firstName: "Имя",
    lastName: "Фамилия",
    birthData: "Дата рождения",
    gender: "Пол",
    region: "Регион",
    contactNumber: "Основной телефон",
    backupNumber: "Резервный телефон",
    nameOrganization: "Организация",
    isActive: "Статус аккаунта",
};

const printable = (key, value, regionOptions) => {
    if (key === "isActive") return value ? "Активен" : "Отключён";
    if (key === "gender") return value === "male" ? "Мужской" : value === "female" ? "Женский" : "Не указан";
    if (key === "region") {
        return regionOptions.find((item) => String(item.value) === String(value))?.name || "Не выбран";
    }
    return value || "—";
};

const EditUserProfileBlock = ({
    userDetails = {},
    setTypeModal,
    isSuperAdmin,
    registerGuard,
    onNavigate,
}) => {
    const dispatch = useDispatch();
    const regionSlice = useSelector((state) => state.region);
    const regionsRaw = regionSlice?.regions || regionSlice?.items || regionSlice?.list || [];

    const regionOptions = useMemo(() => (
        Array.isArray(regionsRaw)
            ? regionsRaw.map((region) => ({
                value: region?._id ?? region?.value ?? region?.id,
                name: region?.name ?? region?.label ?? region?.title ?? "Без названия",
            })).filter((option) => option.value && option.name)
            : []
    ), [regionsRaw]);

    const [initialForm, setInitialForm] = useState(() => buildForm(userDetails));
    const [form, setForm] = useState(() => buildForm(userDetails));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    useEffect(() => {
        if (!regionsRaw.length) dispatch(getRegions());
    }, [dispatch, regionsRaw.length]);

    useEffect(() => {
        const next = buildForm(userDetails);
        setInitialForm(next);
        setForm(next);
        setError("");
        setMessage("");
    }, [userDetails?._id]);

    const isDirty = useMemo(
        () => JSON.stringify(form) !== JSON.stringify(initialForm),
        [form, initialForm]
    );

    const changeDetails = useMemo(() => (
        Object.keys(labels)
            .filter((key) => JSON.stringify(form[key]) !== JSON.stringify(initialForm[key]))
            .map((key) => ({
                label: labels[key],
                before: printable(key, initialForm[key], regionOptions),
                after: printable(key, form[key], regionOptions),
            }))
    ), [form, initialForm, regionOptions]);

    const setField = (key, value) => {
        setForm((current) => ({ ...current, [key]: value }));
        setError("");
        setMessage("");
    };

    const save = useCallback(async () => {
        if (!isSuperAdmin) {
            setError("Недостаточно прав");
            return false;
        }

        if (userDetails.role === "superAdmin") {
            setError("Профиль superAdmin редактируется через собственный кабинет");
            return false;
        }

        if (String(form.firstName || "").trim().length < 3) {
            setError("Имя должно содержать минимум 3 символа");
            return false;
        }

        if (String(form.lastName || "").trim().length < 3) {
            setError("Фамилия должна содержать минимум 3 символа");
            return false;
        }

        if (!form.region) {
            setError("Выберите регион");
            return false;
        }

        if (!isPhoneInputValid(form.contactNumber)) {
            setError("Основной телефон укажите в формате +7 999 123-45-67");
            return false;
        }

        if (!isPhoneInputValid(form.backupNumber)) {
            setError("Резервный телефон укажите в формате +7 999 123-45-67");
            return false;
        }

        setBusy(true);
        setError("");
        setMessage("");

        const result = await dispatch(adminUpdateUserProfile(userDetails._id, form));

        setBusy(false);

        if (!result?.ok) {
            setError(result?.message || "Не удалось сохранить профиль");
            return false;
        }

        const next = result?.user ? buildForm(result.user) : { ...form };
        setInitialForm(next);
        setForm(next);
        setMessage(result?.message || "Профиль сохранён");
        return true;
    }, [dispatch, form, isSuperAdmin, userDetails._id, userDetails.role]);

    const discard = useCallback(() => {
        setForm(initialForm);
        setError("");
        setMessage("");
        return true;
    }, [initialForm]);

    useEffect(() => {
        registerGuard?.({
            isDirty,
            canSave: isDirty && !busy,
            title: "Закрыть редактор без сохранения?",
            text: "В профиле пользователя есть несохранённые изменения.",
            details: changeDetails,
            onSave: save,
            onDiscard: discard,
        });

        return () => registerGuard?.(null);
    }, [busy, changeDetails, discard, isDirty, registerGuard, save]);

    const saveAndReturn = async () => {
        const saved = await save();
        if (saved) setTypeModal("");
    };

    return (
        <div className="userSection">
            <div className="userSection__head">
                <div>
                    <span>Профиль</span>
                    <h3>Редактор профиля</h3>
                    <p>Личные данные, контакты, регион, организация и состояние аккаунта.</p>
                </div>
                {isDirty ? <b className="userSection__dirty">Есть изменения</b> : null}
            </div>

            <div className="userManageForm">
                <div className="userManageForm__notice">
                    Изменения выполняются от имени superAdmin и записываются в аудит профиля.
                </div>

                <div className="userManageForm__grid">
                    <label className="userManageForm__field">
                        <span>Имя</span>
                        <input value={form.firstName || ""} onChange={(e) => setField("firstName", e.target.value)} />
                    </label>
                    <label className="userManageForm__field">
                        <span>Фамилия</span>
                        <input value={form.lastName || ""} onChange={(e) => setField("lastName", e.target.value)} />
                    </label>
                    <label className="userManageForm__field">
                        <span>Дата рождения</span>
                        <input type="date" value={form.birthData || ""} onChange={(e) => setField("birthData", e.target.value)} />
                    </label>
                    <label className="userManageForm__field">
                        <span>Пол</span>
                        <select value={form.gender || ""} onChange={(e) => setField("gender", e.target.value)}>
                            <option value="">Не указан</option>
                            <option value="male">Мужской</option>
                            <option value="female">Женский</option>
                        </select>
                    </label>
                    <label className="userManageForm__field is-full">
                        <span>Регион</span>
                        <select value={form.region || ""} onChange={(e) => setField("region", e.target.value)}>
                            <option value="">Выберите регион</option>
                            {regionOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.name}</option>
                            ))}
                        </select>
                    </label>
                    <label className="userManageForm__field">
                        <span>Основной телефон</span>
                        <input value={form.contactNumber || ""} placeholder="+7 999 123-45-67" onChange={(e) => setField("contactNumber", e.target.value)} />
                    </label>
                    <label className="userManageForm__field">
                        <span>Резервный телефон</span>
                        <input value={form.backupNumber || ""} placeholder="+7 999 123-45-67" onChange={(e) => setField("backupNumber", e.target.value)} />
                    </label>
                    <label className="userManageForm__field is-full">
                        <span>Организация</span>
                        <input value={form.nameOrganization || ""} placeholder="Название организации" onChange={(e) => setField("nameOrganization", e.target.value)} />
                    </label>
                    <label className="userManageForm__field is-full">
                        <span>Статус аккаунта</span>
                        <select value={form.isActive ? "active" : "disabled"} onChange={(e) => setField("isActive", e.target.value === "active")}>
                            <option value="active">Активен</option>
                            <option value="disabled">Отключён</option>
                        </select>
                    </label>
                </div>

                {message ? <div className="userManageForm__message">{message}</div> : null}
                {error ? <div className="userManageForm__error">{error}</div> : null}

                <div className="userManageForm__footer">
                    <button type="button" className="is-secondary" onClick={() => onNavigate?.("")} disabled={busy}>
                        Отмена
                    </button>
                    <button type="button" onClick={saveAndReturn} disabled={busy || !isDirty}>
                        {busy ? "Сохранение..." : "Сохранить изменения"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditUserProfileBlock;
